import { FieldShell } from '@today/ui-components'
import { Button, Dialog, Input } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText } from '../product/product-fields'
import type { OrgMoveOptionView, Translate } from './org-types'

/**
 * Props for {@link OrgMoveDialogView}.
 *
 * @property options - Legal parents matching the query (the unit and its subtree are never offered).
 * @property target - The chosen parent id, `null` for "Make root", `undefined` when nothing is chosen yet.
 */
export interface OrgMoveDialogViewProps {
	t: Translate
	isOpen: boolean
	unitCode: string
	query: string
	options: OrgMoveOptionView[]
	target: string | null | undefined
	error: string | null
	isBusy: boolean
	onQueryChange: (query: string) => void
	onTargetChange: (target: string | null) => void
	onConfirm: () => void
	onClose: () => void
}

const optionClass = (selected: boolean) =>
	`w-full rounded-ds-md px-2 py-1 text-left text-ds-sm ${selected ? 'bg-ds-surface-muted font-semibold' : ''}`

function MoveOptions({ props }: { props: OrgMoveDialogViewProps }): ReactElement {
	const { t } = props
	return (
		<div
			role="listbox"
			aria-label={t('move.title', { code: props.unitCode })}
			className="flex max-h-64 flex-col overflow-y-auto"
		>
			<button
				type="button"
				role="option"
				aria-selected={props.target === null}
				className={optionClass(props.target === null)}
				onClick={() => props.onTargetChange(null)}
			>
				{t('move.makeRoot')}
			</button>
			{props.options.map((option) => (
				<button
					key={option.id}
					type="button"
					role="option"
					aria-selected={props.target === option.id}
					className={optionClass(props.target === option.id)}
					onClick={() => props.onTargetChange(option.id)}
				>
					<span className="font-mono">{option.code}</span> {option.name}
				</button>
			))}
			{props.options.length === 0 ? <p className="text-ds-sm text-ds-text-muted">{t('move.noOptions')}</p> : null}
		</div>
	)
}

/** The "Move to…" parent picker (organism). */
export function OrgMoveDialogView(props: OrgMoveDialogViewProps): ReactElement {
	const { t } = props
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onClose())}
			title={t('move.title', { code: props.unitCode })}
		>
			<div className="flex flex-col gap-3">
				<FieldShell label={t('move.search')}>
					<Input type="search" value={props.query} onChange={(e) => props.onQueryChange(e.target.value)} />
				</FieldShell>
				<MoveOptions props={props} />
				<ErrorText message={props.error} />
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy || props.target === undefined} onPress={props.onConfirm}>
						{t('move.confirm')}
					</Button>
					<Button variant="muted" onPress={props.onClose}>
						{t('move.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
